/**
 * Pure H OS · Pre-Production Gap Closure · Profit & Loss (P&L).
 * Computed live from PRODUCTS_HUB (revenue), UNIT_ECONOMICS (COGS-per-unit),
 * and COST_CENTERS (OpEx). Directional — matches the numbers reported in
 * the Monthly Operating Report (June 2026) as the anchor month.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, SectionHeader } from "../../components/lib";
import { DocumentActions } from "../../components/DocumentActions";
import { PRODUCTS_HUB } from "../../mocks/productsHubData";
import { UNIT_ECONOMICS } from "../../mocks/unitEconomicsData";
import { COST_CENTERS } from "../../mocks/costCentersData";

function Row({ label, value, kind = "normal" }: { label: string; value: number; kind?: "normal" | "total" | "final" }) {
  const bold = kind !== "normal";
  const border = kind !== "normal" ? "1px solid var(--ph-border-subtle)" : "none";
  return (
    <div
      data-testid={`pnl-row-${label.toLowerCase().replace(/\W+/g, "-")}`}
      className="grid grid-cols-2 text-sm px-4 py-2 tabular-nums"
      style={{ borderTop: border, fontWeight: bold ? 600 : 400, color: "var(--ph-text)" }}
    >
      <div>{label}</div>
      <div className="text-right">{value >= 0 ? "" : "−"}EGP {Math.abs(Math.round(value)).toLocaleString()}</div>
    </div>
  );
}

export default function ProfitAndLoss() {
  const totals = useMemo(() => {
    const revenue = PRODUCTS_HUB.reduce((s, p) => s + p.monthlyRevenueEGP, 0);
    const cogs    = PRODUCTS_HUB.reduce((s, p) => {
      const ue = UNIT_ECONOMICS.find((u) => u.sku === p.sku);
      return s + (ue ? ue.cogs * p.unitsShipped : 0);
    }, 0);
    const grossProfit = revenue - cogs;
    const grossMarginPct = revenue > 0 ? (grossProfit / revenue) * 100 : 0;

    // OpEx from Cost Centers — excluding CC-COGS-like buckets.
    const opexRnD       = COST_CENTERS.find((c) => c.code === "CC-RND")?.spentEGP ?? 0;
    const opexOps       = COST_CENTERS.find((c) => c.code === "CC-OPS")?.spentEGP ?? 0;
    const opexMkt       = COST_CENTERS.find((c) => c.code === "CC-MKT")?.spentEGP ?? 0;
    const opexCom       = COST_CENTERS.find((c) => c.code === "CC-COM")?.spentEGP ?? 0;
    const opexFin       = COST_CENTERS.find((c) => c.code === "CC-FIN")?.spentEGP ?? 0;
    const opexWs        = COST_CENTERS.find((c) => c.code === "CC-WS")?.spentEGP ?? 0;
    const opexExec      = COST_CENTERS.find((c) => c.code === "CC-EXEC")?.spentEGP ?? 0;
    const opexAI        = COST_CENTERS.find((c) => c.code === "CC-AI")?.spentEGP ?? 0;

    const totalOpEx = opexRnD + opexOps + opexMkt + opexCom + opexFin + opexWs + opexExec + opexAI;
    const ebitda = grossProfit - totalOpEx;
    return {
      revenue, cogs, grossProfit, grossMarginPct,
      opexRnD, opexOps, opexMkt, opexCom, opexFin, opexWs, opexExec, opexAI,
      totalOpEx, ebitda,
    };
  }, []);

  return (
    <div className="space-y-6" data-testid="pnl-page">
      <Card testId="pnl-summary">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <SectionHeader
            eyebrow="P&L · Auto-generated · Unaudited · Management projection"
            title={`EBITDA · EGP ${Math.round(totals.ebitda).toLocaleString()}`}
          />
          <div className="flex items-center gap-4 flex-wrap">
            <DocumentActions docKey="pnl" testId="pnl-doc-actions" />
            <Link
              to="/pureh/unit-economics"
              className="text-xs"
              style={{ color: "var(--ph-accent)" }}
              data-testid="pnl-link-ue"
            >
              Per-SKU unit economics →
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 mt-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Revenue</div>
            <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: "var(--ph-text)" }}>EGP {Math.round(totals.revenue).toLocaleString()}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>Gross margin</div>
            <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: "var(--ph-text)" }}>{totals.grossMarginPct.toFixed(1)}%</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em]" style={{ color: "var(--ph-muted)" }}>EBITDA margin</div>
            <div className="mt-1 text-lg font-semibold tabular-nums" style={{ color: totals.ebitda >= 0 ? "var(--ph-accent)" : "var(--ph-danger)" }}>
              {totals.revenue > 0 ? ((totals.ebitda / totals.revenue) * 100).toFixed(1) : "—"}%
            </div>
          </div>
        </div>
      </Card>

      <Card padded={false} testId="pnl-table">
        <Row label="Revenue"         value={totals.revenue}     kind="total" />
        <Row label="COGS"            value={-totals.cogs} />
        <Row label="Gross Profit"    value={totals.grossProfit} kind="total" />
        <Row label="R&D"             value={-totals.opexRnD} />
        <Row label="Operations"      value={-totals.opexOps} />
        <Row label="Marketing"       value={-totals.opexMkt} />
        <Row label="Commerce"        value={-totals.opexCom} />
        <Row label="Finance / Legal" value={-totals.opexFin} />
        <Row label="Workspace / HR"  value={-totals.opexWs} />
        <Row label="Executive"       value={-totals.opexExec} />
        <Row label="AI Platform"     value={-totals.opexAI} />
        <Row label="Total OpEx"      value={-totals.totalOpEx} kind="total" />
        <Row label="EBITDA"          value={totals.ebitda}     kind="final" />
      </Card>
    </div>
  );
}
